/**
 * 従業員生成ユーティリティ
 */

import type { Employee, Line } from '@shift-demo/shared';

/**
 * ランダムな日本語氏名を生成（苗字の重複なし）
 */
function generateRandomName(usedSurnames: Set<string>): string {
  const surnames = [
    '田中', '佐藤', '鈴木', '高橋', '渡辺', '伊藤', '山本', '中村', '小林', '加藤',
    '吉田', '山田', '松本', '井上', '木村', '林', '清水', '山崎', '森', '池田',
    '橋本', '石川', '前田', '藤田', '後藤', '中島', '小川', '石井', '斎藤', '近藤'
  ];
  
  const givenNames = [
    '太郎', '次郎', '三郎', '四郎', '花子', '桜子', '美咲', '健太', '翔太', '大輔',
    '直樹', '和也', '拓也', '裕子', '恵子', '明美', '智子', '真理', '由美', '麻衣',
    '雅之', '昭宏', '正治', '純一', '久美', '真由美', '香織', '愛', '望', '舞'
  ];
  
  // 未使用の苗字のみを選択可能にする
  const availableSurnames = surnames.filter(surname => !usedSurnames.has(surname));
  
  if (availableSurnames.length === 0) {
    throw new Error('利用可能な苗字が不足しています');
  }
  
  const selectedSurname = availableSurnames[Math.floor(Math.random() * availableSurnames.length)];
  const selectedGivenName = givenNames[Math.floor(Math.random() * givenNames.length)];
  
  // 使用済み苗字セットに追加
  usedSurnames.add(selectedSurname);
  
  return selectedSurname + selectedGivenName;
}

/**
 * 配列からランダムに指定数の要素を選択
 */
function randomSelect<T>(array: T[], count: number): T[] {
  const shuffled = [...array].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * 従業員25名を生成
 * - 多能工と単能工を同程度生成
 * - 各ラインに最低5名の有資格者を確保
 * - 責任者は存在せず、シフト時に多能工から動的選出
 */
export function generateEmployees(): Employee[] {
  const lines: Line[] = ['A', 'B', 'C', 'D', 'E'];
  const totalEmployees = 25;
  const multiSkilledCount = 12; // 多能工12名（残り13名は単能工）
  
  const employees: Employee[] = [];
  const usedSurnames = new Set<string>(); // 使用済み苗字を管理
  
  // Step 1: 各ラインに最低5名の有資格者を確保するため、まず基本配置
  const lineAssignments: Record<Line, number[]> = {
    A: [],
    B: [],
    C: [],
    D: [],
    E: []
  };
  
  // 各ラインに5名ずつ割り当て（重複あり）
  for (let i = 0; i < totalEmployees; i++) {
    const line = lines[i % lines.length];
    lineAssignments[line].push(i);
  }
  
  // Step 2: 従業員を生成
  for (let i = 0; i < totalEmployees; i++) {
    const isMultiSkilled = i < multiSkilledCount;
    let skills: Line[];
    
    if (isMultiSkilled) {
      // 多能工: 2-4つのスキルをランダムに選択
      const skillCount = Math.floor(Math.random() * 3) + 2; // 2-4個
      skills = randomSelect(lines, skillCount);
    } else {
      // 単能工: 1つのスキルを選択
      // 各ラインに最低人数を確保するため、バランスよく配置
      const lineIndex = (i - multiSkilledCount) % lines.length;
      skills = [lines[lineIndex]];
    }
    
    employees.push({
      id: `emp-${i + 1}`,
      name: generateRandomName(usedSurnames),
      skills
    });
  }
  
  // Step 3: 各ラインの人員数をチェックし、不足している場合は調整
  for (const line of lines) {
    const qualifiedCount = employees.filter(emp => emp.skills.includes(line)).length;
    
    if (qualifiedCount < 5) {
      // 不足している場合、多能工のスキルに追加
      const shortage = 5 - qualifiedCount;
      const multiSkilledEmployees = employees
        .filter(emp => emp.skills.length > 1 && !emp.skills.includes(line))
        .sort(() => Math.random() - 0.5)
        .slice(0, shortage);
      
      multiSkilledEmployees.forEach(emp => {
        emp.skills.push(line);
      });
    }
  }
  
  return employees;
}

/**
 * 従業員統計を取得
 */
export function getEmployeeStats(employees: Employee[]) {
  const multiSkilled = employees.filter(emp => emp.skills.length > 1);
  const singleSkilled = employees.filter(emp => emp.skills.length === 1);
  
  const lineStats: Record<Line, number> = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    E: 0
  };
  
  employees.forEach(emp => {
    emp.skills.forEach(line => {
      lineStats[line]++;
    });
  });
  
  return {
    total: employees.length,
    multiSkilled: multiSkilled.length,
    singleSkilled: singleSkilled.length,
    lineStats
  };
}